import React, { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { formatPrice } from "@/components/ProductCard";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  CurrencyDollar, Receipt, ChartBar, UsersThree, ClockCountdown, Warning, X,
  CheckCircle, XCircle, Bank, CreditCard, CaretDown, Eye, ArrowsClockwise, Truck,
} from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NAVY = "#2D3277";
const ORANGE = "#E8662B";
const PIE_COLORS = ["#2D3277", "#E8662B", "#22a06b", "#e0b400", "#8892d6", "#c0402b"];
const inputCls = "w-full border border-black/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30";
const shortDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit" });

const STATUS_META = {
  pending: { label: "Pago pendiente", cls: "bg-gray-100 text-gray-600" },
  pending_transfer: { label: "Pendiente de verificación", cls: "bg-amber-100 text-amber-700" },
  paid: { label: "Pagado", cls: "bg-[hsl(var(--meli-green))]/15 text-[hsl(var(--meli-green))]" },
  rejected: { label: "Rechazado", cls: "bg-red-100 text-red-600" },
};
const SHIPPING_OPTS = ["Preparando producto", "Enviado", "En camino", "Entregado", "Cancelado"];
const CARRIERS = ["Estafeta", "DHL", "FedEx", "Redpack", "Paquetexpress", "Correos de México", "UPS", "99minutos", "Otro"];

// ==================== REPORTS ====================
export function ReportsTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => { api.get(`/admin/analytics?days=${days}`).then((r) => setData(r.data)); }, [days]);
  if (!data) return <p className="text-muted-foreground">Cargando reportes…</p>;

  const m = data.metrics;
  const cards = [
    { label: "Ingresos totales", value: formatPrice(m.total_revenue), icon: CurrencyDollar, accent: true },
    { label: "Pedidos pagados", value: m.total_orders, icon: Receipt },
    { label: "Ticket promedio", value: formatPrice(m.avg_ticket), icon: ChartBar },
    { label: "Clientes nuevos", value: m.new_customers, icon: UsersThree },
    { label: "Transf. por verificar", value: m.pending_transfers, icon: ClockCountdown, warn: m.pending_transfers > 0 },
    { label: "Bajo / sin stock", value: `${m.low_stock} / ${m.out_of_stock}`, icon: Warning, warn: m.out_of_stock > 0 },
  ];

  return (
    <div data-testid="reports-tab" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-extrabold tracking-tight">Reportes y análisis</h2>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`report-range-${d}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full ${days === d ? "bg-primary text-primary-foreground" : "bg-white text-muted-foreground hover:bg-secondary"}`}>
              {d} días
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="reports-metrics">
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={i} className={`rounded-lg p-4 nb-shadow ${c.accent ? "bg-primary text-primary-foreground" : "bg-white"}`}>
              <Icon weight="bold" size={20} className={c.warn ? "text-destructive" : ""} />
              <p className="text-[11px] mt-2 uppercase tracking-wide font-semibold" style={c.accent ? { color: "rgba(255,255,255,.85)" } : { color: "hsl(var(--muted-foreground))" }}>{c.label}</p>
              <p className="text-lg font-extrabold tracking-tight mt-0.5">{c.value}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-lg nb-shadow p-5">
        <h3 className="font-bold text-sm mb-4">Ingresos por día</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data.revenue_by_day} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="date" tickFormatter={shortDate} fontSize={11} stroke="#999" interval="preserveStartEnd" />
            <YAxis fontSize={11} stroke="#999" width={50} />
            <Tooltip labelFormatter={(v) => shortDate(v)} formatter={(val, name) => name === "revenue" ? [formatPrice(val), "Ingresos"] : [val, "Pedidos"]} />
            <Line type="monotone" dataKey="revenue" stroke={ORANGE} strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-lg nb-shadow p-5">
          <h3 className="font-bold text-sm mb-4">Pedidos por estado</h3>
          {data.orders_by_status.length === 0 ? <p className="text-sm text-muted-foreground">Sin datos</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.orders_by_status} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}
                     label={(e) => `${STATUS_META[e.status]?.label || e.status}: ${e.count}`} labelLine={false} fontSize={10}>
                  {data.orders_by_status.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v, STATUS_META[n]?.label || n]} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-lg nb-shadow p-5">
          <h3 className="font-bold text-sm mb-4">Ingresos por categoría</h3>
          {data.revenue_by_category.length === 0 ? <p className="text-sm text-muted-foreground">Sin datos</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.revenue_by_category} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="category" fontSize={10} stroke="#999" interval={0} angle={-20} textAnchor="end" height={50} />
                <YAxis fontSize={11} stroke="#999" width={50} />
                <Tooltip formatter={(v) => [formatPrice(v), "Ingresos"]} />
                <Bar dataKey="revenue" fill={NAVY} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg nb-shadow p-5">
        <h3 className="font-bold text-sm mb-4">Productos más vendidos</h3>
        {data.top_products.length === 0 ? <p className="text-sm text-muted-foreground">Aún sin ventas</p> : (
          <ResponsiveContainer width="100%" height={Math.max(220, data.top_products.length * 38)}>
            <BarChart data={data.top_products} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
              <XAxis type="number" fontSize={11} stroke="#999" />
              <YAxis type="category" dataKey="product_title" fontSize={11} stroke="#666" width={150} tickFormatter={(v) => v.length > 22 ? v.slice(0, 22) + "…" : v} />
              <Tooltip formatter={(v, n) => n === "units" ? [v, "Unidades"] : [formatPrice(v), "Ingresos"]} />
              <Bar dataKey="units" fill={ORANGE} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ==================== PAYMENTS CONFIG ====================
export function PaymentsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-muted-foreground">Cargando…</p>;
  const pay = s.payments || { mercadopago: {}, transfer: {} };
  const mp = pay.mercadopago || {};
  const tr = pay.transfer || {};
  const ship = s.shipping || { origin: {}, package: {}, carrier: "estafeta" };
  const org = ship.origin || {};
  const pkg = ship.package || {};
  const setMp = (k, v) => setS({ ...s, payments: { ...pay, mercadopago: { ...mp, [k]: v } } });
  const setTr = (k, v) => setS({ ...s, payments: { ...pay, transfer: { ...tr, [k]: v } } });
  const setOrg = (k, v) => setS({ ...s, shipping: { ...ship, origin: { ...org, [k]: v } } });
  const setPkg = (k, v) => setS({ ...s, shipping: { ...ship, package: { ...pkg, [k]: v } } });
  const setCarrier = (v) => setS({ ...s, shipping: { ...ship, carrier: v } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { payments: { mercadopago: mp, transfer: tr }, shipping: { origin: org, package: pkg, carrier: ship.carrier || "estafeta" } });
      toast.success("Configuración guardada");
    } catch { toast.error("Error al guardar"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="payments-tab">
      <div className="bg-white rounded-lg nb-shadow p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm flex items-center gap-2"><CreditCard weight="bold" size={18} className="text-[#009EE3]" /> Mercado Pago</h3>
          <Switch checked={!!mp.enabled} onCheckedChange={(v) => setMp("enabled", v)} data-testid="mp-enabled-toggle" />
        </div>
        <p className="text-xs text-muted-foreground mb-3">Configura tus credenciales de Mercado Pago. Actualmente el pago se procesa en modo simulado; los tokens se guardan para la integración real.</p>
        <div className="space-y-3">
          <div><label className="text-xs font-bold uppercase tracking-wide">Access Token</label><input value={mp.access_token || ""} onChange={(e) => setMp("access_token", e.target.value)} placeholder="APP_USR-..." data-testid="mp-access-token" className={inputCls} /></div>
          <div><label className="text-xs font-bold uppercase tracking-wide">Public Key</label><input value={mp.public_key || ""} onChange={(e) => setMp("public_key", e.target.value)} placeholder="APP_USR-..." data-testid="mp-public-key" className={inputCls} /></div>
        </div>
      </div>

      <div className="bg-white rounded-lg nb-shadow p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm flex items-center gap-2"><Bank weight="bold" size={18} className="text-[#2D3277]" /> Transferencia bancaria</h3>
          <Switch checked={!!tr.enabled} onCheckedChange={(v) => setTr("enabled", v)} data-testid="transfer-enabled-toggle" />
        </div>
        <p className="text-xs text-muted-foreground mb-3">Los clientes verán estos datos al pagar y subirán el comprobante. Verificas la transferencia antes de confirmar el pedido.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="text-xs font-bold uppercase tracking-wide">Banco</label><input value={tr.bank_name || ""} onChange={(e) => setTr("bank_name", e.target.value)} data-testid="transfer-bank" className={inputCls} /></div>
          <div><label className="text-xs font-bold uppercase tracking-wide">Titular</label><input value={tr.account_holder || ""} onChange={(e) => setTr("account_holder", e.target.value)} data-testid="transfer-holder" className={inputCls} /></div>
          <div><label className="text-xs font-bold uppercase tracking-wide">CBU / CVU</label><input value={tr.cbu || ""} onChange={(e) => setTr("cbu", e.target.value)} data-testid="transfer-cbu" className={inputCls} /></div>
          <div><label className="text-xs font-bold uppercase tracking-wide">Alias</label><input value={tr.alias || ""} onChange={(e) => setTr("alias", e.target.value)} data-testid="transfer-alias" className={inputCls} /></div>
          <div className="sm:col-span-2"><label className="text-xs font-bold uppercase tracking-wide">Instrucciones (opcional)</label><textarea rows={2} value={tr.instructions || ""} onChange={(e) => setTr("instructions", e.target.value)} placeholder="Ej. Enviar comprobante indicando el número de pedido." className={inputCls} /></div>
        </div>
      </div>

      <div className="bg-white rounded-lg nb-shadow p-5">
        <h3 className="font-bold text-sm flex items-center gap-2 mb-1"><Truck weight="bold" size={18} className="text-[#2D3277]" /> Envíos (Envia.com) — origen y paquete</h3>
        <p className="text-xs text-muted-foreground mb-3">Datos para generar guías automáticamente al despachar un pedido. La guía se crea con estos datos + la dirección del cliente.</p>
        <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">Dirección de origen</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <input value={org.name || ""} onChange={(e) => setOrg("name", e.target.value)} placeholder="Nombre / Empresa" data-testid="ship-origin-name" className={inputCls} />
          <input value={org.phone || ""} onChange={(e) => setOrg("phone", e.target.value)} placeholder="Teléfono" data-testid="ship-origin-phone" className={inputCls} />
          <input value={org.street || ""} onChange={(e) => setOrg("street", e.target.value)} placeholder="Calle y número" data-testid="ship-origin-street" className={inputCls} />
          <input value={org.postalCode || ""} onChange={(e) => setOrg("postalCode", e.target.value)} placeholder="Código postal" data-testid="ship-origin-cp" className={inputCls} />
          <input value={org.city || ""} onChange={(e) => setOrg("city", e.target.value)} placeholder="Ciudad" data-testid="ship-origin-city" className={inputCls} />
          <input value={org.state || ""} onChange={(e) => setOrg("state", e.target.value)} placeholder="Estado (ej. NL, CX, JAL)" data-testid="ship-origin-state" className={inputCls} />
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">Paquete por defecto</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
          <div><label className="text-[10px] text-muted-foreground">Peso (kg)</label><input type="number" step="0.1" value={pkg.weight ?? ""} onChange={(e) => setPkg("weight", e.target.value)} data-testid="ship-pkg-weight" className={inputCls} /></div>
          <div><label className="text-[10px] text-muted-foreground">Largo (cm)</label><input type="number" value={pkg.length ?? ""} onChange={(e) => setPkg("length", e.target.value)} data-testid="ship-pkg-length" className={inputCls} /></div>
          <div><label className="text-[10px] text-muted-foreground">Ancho (cm)</label><input type="number" value={pkg.width ?? ""} onChange={(e) => setPkg("width", e.target.value)} data-testid="ship-pkg-width" className={inputCls} /></div>
          <div><label className="text-[10px] text-muted-foreground">Alto (cm)</label><input type="number" value={pkg.height ?? ""} onChange={(e) => setPkg("height", e.target.value)} data-testid="ship-pkg-height" className={inputCls} /></div>
          <div className="col-span-2"><label className="text-[10px] text-muted-foreground">Contenido</label><input value={pkg.content || ""} onChange={(e) => setPkg("content", e.target.value)} placeholder="Ej. Ropa" data-testid="ship-pkg-content" className={inputCls} /></div>
          <div className="col-span-2">
            <label className="text-[10px] text-muted-foreground">Paquetería por defecto</label>
            <Select value={ship.carrier || "estafeta"} onValueChange={setCarrier}>
              <SelectTrigger data-testid="ship-default-carrier" className="rounded-md text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-md">{["estafeta", "fedex", "dhl", "redpack", "paquetexpress", "carssa", "ups"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="save-payments-button" className="nb-btn bg-primary text-primary-foreground px-6 py-2.5 hover:brightness-110 disabled:opacity-50">{saving ? "Guardando…" : "Guardar configuración"}</button>
    </div>
  );
}

// ==================== ORDERS ADMIN ====================
export function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    const url = filter ? `/admin/orders?status=${filter}` : "/admin/orders";
    setOrders((await api.get(url)).data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const verify = async (id, approve) => {
    let reason = "";
    if (!approve) {
      reason = window.prompt("Motivo del rechazo:", "Comprobante no válido") || "Comprobante no válido";
    } else if (!window.confirm("¿Confirmar que la transferencia fue recibida? Se descontará el stock y el pedido pasará a Pagado.")) {
      return;
    }
    try {
      await api.post(`/admin/orders/${id}/verify`, { approve, reason });
      toast.success(approve ? "Transferencia aprobada" : "Transferencia rechazada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const changeShipping = async (id, status) => {
    try { await api.put(`/admin/orders/${id}/shipping-status`, { shipping_status: status }); toast.success("Estado de envío actualizado"); load(); }
    catch { toast.error("Error"); }
  };

  const saveTracking = async (id) => {
    const d = drafts[id] || {};
    try {
      await api.put(`/admin/orders/${id}/shipping-status`, { carrier: d.carrier ?? "", tracking_number: d.tracking_number ?? "" });
      toast.success("Guía guardada");
      load();
    } catch { toast.error("Error al guardar la guía"); }
  };
  const setDraft = (id, key, val, order) => setDrafts((s) => ({ ...s, [id]: { carrier: order.carrier || "", tracking_number: order.tracking_number || "", ...s[id], [key]: val } }));

  const syncTrack = async (id) => {
    try { await api.post(`/orders/${id}/track-sync`); toast.success("Estatus actualizado desde Envia.com"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo actualizar el estatus"); }
  };

  const [genLoading, setGenLoading] = useState(null);
  const createShipment = async (id) => {
    if (!window.confirm("Se generará una guía real en Envia.com con la paquetería configurada. ¿Continuar?")) return;
    setGenLoading(id);
    try { await api.post(`/admin/orders/${id}/create-shipment`, {}); toast.success("Guía generada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo generar la guía", { duration: 7000 }); }
    finally { setGenLoading(null); }
  };

  const FILTERS = [["", "Todos"], ["pending_transfer", "Por verificar"], ["paid", "Pagados"], ["rejected", "Rechazados"], ["pending", "Pago pendiente"]];

  return (
    <div data-testid="admin-orders">
      <div className="flex gap-1 mb-4 flex-wrap">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`order-filter-${k || "all"}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full ${filter === k ? "bg-primary text-primary-foreground" : "bg-white text-muted-foreground hover:bg-secondary"}`}>{label}</button>
        ))}
      </div>

      {loading ? <p className="text-muted-foreground">Cargando…</p> : orders.length === 0 ? (
        <div className="bg-white rounded-lg nb-shadow p-8 text-center text-muted-foreground">Sin pedidos</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const meta = STATUS_META[o.status] || STATUS_META.pending;
            const isTransfer = o.payment_type === "transfer";
            return (
              <div key={o.id} className="bg-white rounded-lg nb-shadow overflow-hidden" data-testid={`admin-order-${o.id}`}>
                <button onClick={() => setOpen(open === o.id ? null : o.id)} className="w-full p-4 flex items-center gap-3 text-left hover:bg-secondary/30">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm">{o.order_number || o.id.slice(0, 8)}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${meta.cls}`}>{meta.label}</span>
                      {isTransfer && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#2D3277]/10 text-[#2D3277] font-semibold">Transferencia</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{o.buyer_name} · {new Date(o.created_at).toLocaleString("es-MX")}</p>
                  </div>
                  <span className="font-bold text-sm">{formatPrice(o.total)}</span>
                  <CaretDown weight="bold" size={16} className={`transition-transform ${open === o.id ? "rotate-180" : ""}`} />
                </button>

                {open === o.id && (
                  <div className="border-t border-black/10 p-4 space-y-4 text-sm">
                    {isTransfer && o.status === "pending_transfer" && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="font-semibold text-amber-800">Verificar transferencia</p>
                          <p className="text-xs text-amber-700">Revisa el comprobante y confirma que recibiste el pago.</p>
                        </div>
                        <div className="flex gap-2">
                          {o.receipt_image && <button onClick={() => setReceipt(o.receipt_image)} data-testid={`view-receipt-${o.id}`} className="nb-btn bg-white border border-black/10 px-3 py-1.5 text-xs flex items-center gap-1"><Eye weight="bold" size={14} /> Ver comprobante</button>}
                          <button onClick={() => verify(o.id, true)} data-testid={`approve-order-${o.id}`} className="nb-btn bg-[hsl(var(--meli-green))] text-white px-3 py-1.5 text-xs flex items-center gap-1"><CheckCircle weight="bold" size={14} /> Aprobar</button>
                          <button onClick={() => verify(o.id, false)} data-testid={`reject-order-${o.id}`} className="nb-btn bg-destructive text-white px-3 py-1.5 text-xs flex items-center gap-1"><XCircle weight="bold" size={14} /> Rechazar</button>
                        </div>
                      </div>
                    )}
                    {o.status === "rejected" && <p className="text-xs text-red-600">Rechazado: {o.rejection_reason}{o.verified_by ? ` · por ${o.verified_by}` : ""}</p>}
                    {o.receipt_image && o.status !== "pending_transfer" && (
                      <button onClick={() => setReceipt(o.receipt_image)} className="text-xs text-primary hover:underline flex items-center gap-1"><Eye weight="bold" size={14} /> Ver comprobante</button>
                    )}

                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Productos</p>
                      {o.items.map((it, i) => (
                        <div key={i} className="flex items-center gap-3 py-1">
                          <img src={it.product_image ? fileUrl(it.product_image) : ""} alt="" className="w-10 h-10 rounded object-cover bg-secondary" />
                          <span className="flex-1">{it.product_title}{it.variant_name && <span className="text-muted-foreground"> · {it.variant_name}</span>} <span className="text-muted-foreground">×{it.quantity}</span></span>
                          <span className="font-semibold">{formatPrice(it.subtotal)}</span>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Cliente</p>
                        <p>{o.buyer_name}</p>
                        <p className="text-xs text-muted-foreground">{o.buyer_email}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Envío a</p>
                        {o.shipping_address ? (
                          <p className="text-xs">{o.shipping_address.recipient || o.buyer_name}<br />{[o.shipping_address.street, o.shipping_address.number].filter(Boolean).join(" ")}{o.shipping_address.apartment ? `, ${o.shipping_address.apartment}` : ""}<br />{[o.shipping_address.city, o.shipping_address.state, o.shipping_address.zip].filter(Boolean).join(", ")}<br />{o.shipping_address.phone}</p>
                        ) : <p className="text-xs text-muted-foreground">No especificada</p>}
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Facturación</p>
                        {o.billing ? (
                          <p className="text-xs">{o.billing.tax_condition}<br />{o.billing.full_name}<br />{o.billing.doc_type} {o.billing.doc_number}</p>
                        ) : <p className="text-xs text-muted-foreground">Consumidor final</p>}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-end justify-between gap-3 border-t border-black/10 pt-3">
                      <div className="text-xs space-y-1">
                        <p><span className="text-muted-foreground">Pago:</span> {o.payment_method}</p>
                        {o.status === "paid" && (
                          <div className="mt-1 space-y-2 w-full">
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">Estado envío:</span>
                              <Select value={o.shipping_status} onValueChange={(v) => changeShipping(o.id, v)}>
                                <SelectTrigger data-testid={`shipping-status-${o.id}`} className="h-7 w-auto text-xs rounded-md"><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-md">{SHIPPING_OPTS.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div className="flex items-end gap-2 flex-wrap">
                              <div>
                                <label className="text-[10px] text-muted-foreground block">Paquetería</label>
                                <Select value={(drafts[o.id]?.carrier ?? o.carrier) || ""} onValueChange={(v) => setDraft(o.id, "carrier", v, o)}>
                                  <SelectTrigger data-testid={`carrier-${o.id}`} className="h-7 w-40 text-xs rounded-md"><SelectValue placeholder="Elegir" /></SelectTrigger>
                                  <SelectContent className="rounded-md">{CARRIERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-[10px] text-muted-foreground block">Número de guía</label>
                                <input value={(drafts[o.id]?.tracking_number ?? o.tracking_number) || ""} onChange={(e) => setDraft(o.id, "tracking_number", e.target.value, o)} data-testid={`tracking-${o.id}`} placeholder="Ej. 1234567890" className="h-7 w-44 border border-black/15 rounded-md px-2 text-xs" />
                              </div>
                              <button onClick={() => saveTracking(o.id)} data-testid={`save-tracking-${o.id}`} className="nb-btn bg-primary text-primary-foreground h-7 px-3 text-xs hover:brightness-110">Guardar guía</button>
                            </div>
                            {!o.tracking_number && (
                              <button onClick={() => createShipment(o.id)} disabled={genLoading === o.id} data-testid={`generate-guide-${o.id}`} className="nb-btn bg-[#E8662B] text-white h-7 px-3 text-xs flex items-center gap-1 hover:brightness-110 disabled:opacity-50">
                                <Truck weight="bold" size={13} /> {genLoading === o.id ? "Generando…" : "Generar guía con Envia"}
                              </button>
                            )}
                            {o.tracking_number && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-[11px] text-muted-foreground">Guía actual: <b>{o.carrier}</b> · {o.tracking_number}</p>
                                {o.label_url && <a href={o.label_url} target="_blank" rel="noreferrer" data-testid={`label-${o.id}`} className="text-[11px] text-primary hover:underline">Descargar etiqueta</a>}
                                <button onClick={() => syncTrack(o.id)} data-testid={`sync-track-${o.id}`} className="nb-btn bg-[#2D3277] text-white h-6 px-2.5 text-[11px] flex items-center gap-1 hover:brightness-110"><ArrowsClockwise weight="bold" size={12} /> Actualizar desde Envia</button>
                                {o.track_url && <a href={o.track_url} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline">Ver en Envia</a>}
                                {o.last_tracked_at && <span className="text-[10px] text-muted-foreground">act. {new Date(o.last_tracked_at).toLocaleString("es-MX")}</span>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="w-48 space-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatPrice(o.subtotal)}</span></div>
                        {o.discount > 0 && <div className="flex justify-between text-[hsl(var(--meli-green))]"><span>Descuento</span><span>-{formatPrice(o.discount)}</span></div>}
                        <div className="flex justify-between"><span className="text-muted-foreground">Envío</span><span>{o.shipping === 0 ? "Gratis" : formatPrice(o.shipping)}</span></div>
                        <div className="flex justify-between font-bold border-t border-black/10 pt-1"><span>Total</span><span>{formatPrice(o.total)}</span></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {receipt && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={() => setReceipt(null)} data-testid="receipt-modal">
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setReceipt(null)} className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 nb-shadow"><X weight="bold" size={18} /></button>
            <img src={fileUrl(receipt)} alt="Comprobante" className="w-full rounded-lg" />
          </div>
        </div>
      )}
    </div>
  );
}
